import { useState } from "react";
import { visionURL } from "../../config/environment";
import JsonApiService from "../../services/jsonapiService";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import {
  buildCourseOverviewRoute,
  buildCoursePlayerRoute,
  buildContentViewerRoute
} from "../../routes/LearningCenterRoutes";
import Jsona from "jsona";
import normalize from "../../redux/utils/normalize";
import {
  updateUserAssignmentSuccess,
  deleteUserAssignmentSuccess,
  updateDisplayedCourse,
  updateUserAssignmentBegin,
  fetchUserAssignmentsListBegin,
  fetchUserAssignmentsListSuccess,
  fetchUserAssignmentsListFailure
} from "../../redux/actions/userAssignmentsListActions";
import visionCallService from "../../services/visionCallService";
import { throttle } from "lodash";

const dataFormatter = new Jsona();

const myCoursesApiService = async () =>
  new JsonApiService("my_courses", {
    baseUrl: `${visionURL}/learning_center/api`,
    credentials: "include"
  });

const courseContentApiService = async () =>
  new JsonApiService("course_content", {
    baseUrl: `${visionURL}/learning_center/api`,
    credentials: "include"
  });

export const useEnrollments = () => {
  const dispatch = useDispatch();
  const [isEnrolling, setIsEnrolling] = useState(false);

  const enrollIntoCourse = throttle(
    async (userAssignmentId, releaseId, navigation = false, openInNewTab) => {
      if (!isEnrolling) {
        setIsEnrolling(true);
        const service = await myCoursesApiService();
        const response = await service
          .customParams({ releaseId })
          .updateRelated(
            userAssignmentId,
            "enroll",
            visionCallService.serializeRequestBody(
              { releaseId },
              "userAssignment"
            )
          )
          .then(response => response.json())
          .catch(error => {
            setIsEnrolling(false);
            throw error;
          });

        if (response.error) {
          setIsEnrolling(false);
          throw response.error;
        }
        const normalizedResponse = normalize(response);
        dispatch(updateUserAssignmentSuccess(normalizedResponse));
        const enrollmentData = dataFormatter.deserialize(response);
        const {
          assignedTrainingObjectId,
          enrolledTrainingObjectId
        } = enrollmentData;

        if (navigation) {
          let courseRoute = null;
          if (
            assignedTrainingObjectId &&
            enrolledTrainingObjectId &&
            navigation == "overview"
          ) {
            courseRoute = buildCourseOverviewRoute(
              assignedTrainingObjectId,
              enrolledTrainingObjectId
            );
          } // navigate to course player, no getting started exists, navigate to table of contents
          else if (navigation == "first") {
            const contentService = await courseContentApiService();
            const content = await contentService
              .include("course_sections", "course_sections.course_contentables")
              .find(enrolledTrainingObjectId)
              .then(content => content.json());
            const { included } = content;
            const contentSort = included.filter(index => {
              return index.attributes.sectionType != "optional";
            });
            const courseContentId =
              contentSort[0].relationships.courseContentables.data[0].id;
            courseRoute = buildContentViewerRoute(
              userAssignmentId,
              courseContentId
            );
          } else {
            courseRoute = buildCoursePlayerRoute(assignedTrainingObjectId);
          }

          if (openInNewTab) {
            window.open(courseRoute, "_blank");
          } else {
            dispatch(push(courseRoute));
          }
        }

        setIsEnrolling(false);
        return enrollmentData;
      }
    },
    500,
    { trailing: false }
  );
  return { enrollIntoCourse };
};

export const useCourse = () => {
  const dispatch = useDispatch();
  const fetchCourse = async trainingObjectId => {
    dispatch(fetchUserAssignmentsListBegin());
    const service = await myCoursesApiService();
    const response = await service
      .find(trainingObjectId)
      .then(response => response.json());
    if (response.error) {
      dispatch(fetchUserAssignmentsListFailure(normalize(response.error)));
      throw response.error;
    }
    dispatch(fetchUserAssignmentsListSuccess(normalize(response)));
    return response;
  };
  return { fetchCourse };
};

export const useUserAssignments = () => {
  const dispatch = useDispatch();

  const createUserAssignment = async (
    trainingObjectId,
    forceEnrollment,
    releaseId = null,
    navigation = null
  ) => {
    dispatch(updateUserAssignmentBegin());
    const service = await myCoursesApiService();
    const response = await service
      .create(
        visionCallService.serializeRequestBody(
          { trainingObjectId, forceEnrollment, releaseId },
          "userAssignment"
        )
      )
      .then(response => response.json())
      .catch(error => {
        throw error;
      });

    if (response.error) {
      throw response.error;
    }
    const normalizedResponse = normalize(response);
    dispatch(updateUserAssignmentSuccess(normalizedResponse));
    const enrollmentData = dataFormatter.deserialize(response);
    const {
      assignedTrainingObjectId,
      enrolledTrainingObjectId,
      id
    } = enrollmentData;

    if (navigation) {
      if (navigation == "overview") {
        dispatch(
          push(
            buildCourseOverviewRoute(
              assignedTrainingObjectId,
              enrolledTrainingObjectId
            )
          )
        );
      } // navigate to course player, no getting started exists, navigate to table of contents
      else if (navigation == "first") {
        const contentService = await courseContentApiService();
        const content = await contentService
          .include("course_sections", "course_sections.course_contentables")
          .find(enrolledTrainingObjectId)
          .then(content => content.json());
        const { included } = content;
        const contentSort = included.filter(index => {
          return index.attributes.sectionType != "optional";
        });
        const courseContentId =
          contentSort[0].relationships.courseContentables.data[0].id;
        dispatch(push(buildContentViewerRoute(id, courseContentId)));
      } else {
        dispatch(push(buildCoursePlayerRoute(assignedTrainingObjectId)));
      }
    }
    return response;
  };

  const deleteUserAssignment = async userAssignmentId => {
    const service = await myCoursesApiService();
    const response = await service
      .delete(userAssignmentId)
      .then(response => response.json());
    dispatch(deleteUserAssignmentSuccess(userAssignmentId));
    return response;
  };

  const updateUserAssignment = async (
    userAssignmentId,
    releaseId,
    navigation = false
  ) => {
    dispatch(updateUserAssignmentBegin(userAssignmentId));
    const service = await myCoursesApiService();
    const response = await service.update(
      userAssignmentId,
      visionCallService.serializeRequestBody({ releaseId }, "userAssignment")
    );

    if (response.status === 204) {
      // if there's no response body, the user assignment was deleted
      dispatch(deleteUserAssignmentSuccess(userAssignmentId));
    } else {
      const json = await response.json();
      if (json.error) {
        throw json.error;
      }
      if (response.ok) {
        dispatch(updateUserAssignmentSuccess(normalize(json)));
        const newUserAssignmentId = json.data.id;
        // In the case of self assignments, a new user assignment will be returned
        if (newUserAssignmentId !== userAssignmentId) {
          dispatch(
            updateDisplayedCourse("self", newUserAssignmentId, userAssignmentId)
          );
          dispatch(deleteUserAssignmentSuccess(userAssignmentId));
        }

        const enrollmentData = dataFormatter.deserialize(json);

        const {
          assignedTrainingObjectId,
          enrolledTrainingObjectId
        } = enrollmentData;

        if (navigation) {
          if (
            assignedTrainingObjectId &&
            enrolledTrainingObjectId &&
            navigation == "overview"
          ) {
            dispatch(
              push(
                buildCourseOverviewRoute(
                  assignedTrainingObjectId,
                  enrolledTrainingObjectId
                )
              )
            );
          } // navigate to course player, no getting started exists, navigate to first activity
          else if (navigation == "first") {
            const contentService = await courseContentApiService();
            const content = await contentService
              .include("course_sections", "course_sections.course_contentables")
              .find(enrolledTrainingObjectId)
              .then(content => content.json());
            const courseContentId =
              content.included[0].relationships.courseContentables.data[0].id;
            dispatch(
              push(buildContentViewerRoute(userAssignmentId, courseContentId))
            );
          } else {
            dispatch(push(buildCoursePlayerRoute(assignedTrainingObjectId)));
          }
        }
      }
    }
    return response;
  };

  return { createUserAssignment, deleteUserAssignment, updateUserAssignment };
};
